import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useLanguage, API } from "../App";
import { useIsDentalTenant } from "../hooks/useIsDentalTenant";
import { useIsProTenant }    from "../hooks/useIsProTenant";
import AdminLayout from "../components/AdminLayout";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../components/ui/dialog";
import { 
  Plus, 
  Search, 
  Mail, 
  Phone, 
  Calendar,
  TrendingUp,
  Star,
  Clock,
  Download,
  Wallet,
  Activity,
  AlertTriangle,
  Sparkles,
  RefreshCw,
  ScanLine,
  ChevronRight,
  Crown,
  FileSignature
} from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { format } from "date-fns";
import { it, enUS } from "date-fns/locale";

const SEGMENT_COLORS = {
  new: { bg: "bg-slate-100 dark:bg-slate-800", text: "text-slate-600 dark:text-slate-300", border: "border-slate-300 dark:border-slate-600", dot: "#94A3B8" },
  low: { bg: "bg-red-50 dark:bg-red-950/30", text: "text-red-700 dark:text-red-300", border: "border-red-300 dark:border-red-700", dot: "#EF4444" },
  medium: { bg: "bg-orange-50 dark:bg-orange-950/30", text: "text-orange-700 dark:text-orange-300", border: "border-orange-300 dark:border-orange-700", dot: "#F97316" },
  high: { bg: "bg-emerald-50 dark:bg-emerald-950/30", text: "text-emerald-700 dark:text-emerald-300", border: "border-emerald-300 dark:border-emerald-700", dot: "#22C55E" },
  at_risk: { bg: "bg-violet-50 dark:bg-violet-950/30", text: "text-violet-700 dark:text-violet-300", border: "border-violet-300 dark:border-violet-700", dot: "#8B5CF6" },
  vip: { bg: "bg-amber-50 dark:bg-amber-950/30", text: "text-amber-700 dark:text-amber-300", border: "border-amber-300 dark:border-amber-700", dot: "#FFD700" },
};

const SEGMENT_LABELS = {
  it: { new: "Nuovo", low: "Basso Valore", medium: "Medio", high: "Alto Valore", at_risk: "A Rischio", vip: "VIP" },
  en: { new: "New", low: "Low Value", medium: "Medium", high: "High Value", at_risk: "At Risk", vip: "VIP" },
};

const SegmentBadge = ({ segment, language }) => {
  const colors = SEGMENT_COLORS[segment] || SEGMENT_COLORS.new;
  const label = (SEGMENT_LABELS[language] || SEGMENT_LABELS.it)[segment] || segment;
  return (
    <span 
      className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold border ${colors.bg} ${colors.text} ${colors.border}`}
      data-testid={`segment-badge-${segment}`}
    >
      <span className="w-2 h-2 rounded-full" style={{ backgroundColor: colors.dot }} />
      {label}
    </span>
  );
};

const Clients = () => {
  const navigate = useNavigate();
  const { t, language } = useLanguage();
  const isDental = useIsDentalTenant();
  const isPro    = useIsProTenant();
  const canScanIntake = isDental || isPro;
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedTag, setSelectedTag] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedClient, setSelectedClient] = useState(null);
  const [clientDetails, setClientDetails] = useState(null);
  const [ltvData, setLtvData] = useState(null);
  const [ltvLoading, setLtvLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    notes: "",
    // Dental-only fields (autocompiled by tessera-sanitaria scanner)
    first_name: "",
    last_name: "",
    fiscal_code: "",
    birth_date: "",
    gender: "",
    birth_place: "",
  });
  const [scanMode, setScanMode] = useState(null);  // null | "upload" | "camera" | "cf"
  const [scanBusy, setScanBusy] = useState(false);
  const [scanResult, setScanResult] = useState(null); // {confidence, document_type, ...}
  const [duplicates, setDuplicates] = useState([]);   // existing clients matching CF/email/phone
  const [cfInput, setCfInput] = useState("");
  const cameraVideoRef = React.useRef(null);
  const cameraStreamRef = React.useRef(null);

  const tags = [
    { value: "", label: language === "it" ? "Tutti" : "All" },
    { value: "new", label: language === "it" ? "Nuovi" : "New" },
    { value: "vip", label: "VIP" },
    { value: "frequent", label: language === "it" ? "Frequenti" : "Frequent" },
    { value: "inactive", label: language === "it" ? "Inattivi" : "Inactive" }
  ];

  useEffect(() => {
    fetchClients();
  }, [searchQuery, selectedTag]);

  const fetchClients = async () => {
    try {
      const params = {};
      if (searchQuery) params.search = searchQuery;
      if (selectedTag) params.tag = selectedTag;
      
      const response = await axios.get(`${API}/clients`, { 
        params,
        withCredentials: true 
      });
      setClients(response.data);
    } catch (error) {
      toast.error(language === "it" ? "Errore nel caricamento" : "Loading error");
    } finally {
      setLoading(false);
    }
  };

  const fetchClientDetails = async (clientId) => {
    try {
      const response = await axios.get(`${API}/clients/${clientId}`, {
        withCredentials: true
      });
      setClientDetails(response.data);
    } catch (error) {
      toast.error(language === "it" ? "Errore" : "Error");
    }
  };

  const fetchClientLTV = async (clientId) => {
    setLtvLoading(true);
    setLtvData(null);
    try {
      const response = await axios.get(`${API}/ltv/client/${clientId}`, {
        withCredentials: true
      });
      setLtvData(response.data);
    } catch (error) {
      console.error("LTV fetch error:", error);
    } finally {
      setLtvLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      await axios.post(`${API}/clients`, formData, { withCredentials: true });
      toast.success(language === "it" ? "Cliente creato" : "Client created");
      
      setDialogOpen(false);
      resetForm();
      fetchClients();
    } catch (error) {
      toast.error(error.response?.data?.detail || (language === "it" ? "Errore" : "Error"));
    }
  };

  const resetForm = () => {
    setFormData({
      name: "",
      email: "",
      phone: "",
      notes: "",
      first_name: "",
      last_name: "",
      fiscal_code: "",
      birth_date: "",
      gender: "",
      birth_place: "",
    });
    setScanMode(null);
    setScanResult(null);
    setCfInput("");
    setDuplicates([]);
    _stopCamera();
  };

  // ============================================================
  // TESSERA SANITARIA — Scan + OCR (dental only)
  // ============================================================
  const _stopCamera = () => {
    try {
      const s = cameraStreamRef.current;
      if (s) s.getTracks().forEach(t => t.stop());
    } catch { /* noop */ }
    cameraStreamRef.current = null;
  };

  const _applyOcr = (data) => {
    if (!data) return;
    const first = data.first_name || "";
    const last  = data.last_name  || "";
    const full  = [first, last].filter(Boolean).join(" ").trim();
    setFormData(f => ({
      ...f,
      first_name:  first  || f.first_name,
      last_name:   last   || f.last_name,
      name:        full   || f.name,
      fiscal_code: data.fiscal_code || f.fiscal_code,
      birth_date:  data.birth_date  || f.birth_date,
      gender:      data.gender      || f.gender,
      birth_place: data.birth_place || f.birth_place,
    }));
    setScanResult({
      confidence:    data.confidence || "medium",
      document_type: data.document_type || "altro",
      fiscal_code_valid: !!data.fiscal_code_valid,
    });
    toast.success(language === "it" ? "Dati estratti dalla tessera!" : "Data extracted from card!");
    _checkDuplicates({
      fiscal_code: data.fiscal_code,
      first_name:  first,
      last_name:   last,
    });
  };

  const _checkDuplicates = async ({ fiscal_code, first_name, last_name, email, phone } = {}) => {
    if (!fiscal_code && !email && !phone && !(first_name && last_name)) {
      setDuplicates([]);
      return;
    }
    try {
      const r = await axios.post(`${API}/dental/intake/check-dup`, {
        fiscal_code: fiscal_code || null,
        email:       email       || null,
        phone:       phone       || null,
        first_name:  first_name  || null,
        last_name:   last_name   || null,
      }, { withCredentials: true });
      setDuplicates(r.data?.duplicates || []);
    } catch { /* silent */ }
  };

  const handleUploadScan = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setScanBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await axios.post(`${API}/dental/intake/scan`, fd, {
        withCredentials: true,
        headers: { "Content-Type": "multipart/form-data" },
      });
      _applyOcr(r.data);
      setScanMode(null);
    } catch (err) {
      toast.error(err.response?.data?.detail || (language === "it" ? "Scansione fallita" : "Scan failed"));
    } finally {
      setScanBusy(false);
      try { e.target.value = ""; } catch { /* noop */ }
    }
  };

  const handleStartCamera = async () => {
    setScanMode("camera");
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: { ideal: "environment" }, width: 1280, height: 720 },
      });
      cameraStreamRef.current = stream;
      if (cameraVideoRef.current) {
        cameraVideoRef.current.srcObject = stream;
        await cameraVideoRef.current.play();
      }
    } catch (err) {
      toast.error(language === "it" ? "Impossibile accedere alla fotocamera" : "Camera access denied");
      setScanMode(null);
    }
  };

  const handleCaptureCamera = async () => {
    const video = cameraVideoRef.current;
    if (!video) return;
    setScanBusy(true);
    try {
      const canvas = document.createElement("canvas");
      canvas.width  = video.videoWidth;
      canvas.height = video.videoHeight;
      canvas.getContext("2d").drawImage(video, 0, 0);
      const blob = await new Promise(res => canvas.toBlob(res, "image/jpeg", 0.92));
      const fd = new FormData();
      fd.append("file", new File([blob], "tessera.jpg", { type: "image/jpeg" }));
      const r = await axios.post(`${API}/dental/intake/scan`, fd, {
        withCredentials: true,
        headers: { "Content-Type": "multipart/form-data" },
      });
      _applyOcr(r.data);
      _stopCamera();
      setScanMode(null);
    } catch (err) {
      toast.error(err.response?.data?.detail || (language === "it" ? "Scansione fallita" : "Scan failed"));
    } finally { setScanBusy(false); }
  };

  const handleEnrichCF = async () => {
    const cf = (cfInput || "").trim().toUpperCase();
    if (cf.length !== 16) {
      toast.error(language === "it" ? "Codice fiscale di 16 caratteri richiesto" : "16-char fiscal code required");
      return;
    }
    setScanBusy(true);
    try {
      const r = await axios.post(`${API}/dental/intake/enrich-cf`,
        { fiscal_code: cf }, { withCredentials: true });
      if (!r.data?.fiscal_code_valid) {
        toast.error(language === "it" ? "Codice fiscale non valido" : "Invalid fiscal code");
        return;
      }
      setFormData(f => ({
        ...f,
        fiscal_code: r.data.fiscal_code,
        birth_date:  r.data.birth_date  || f.birth_date,
        gender:      r.data.gender      || f.gender,
        birth_place: r.data.birth_place || f.birth_place,
      }));
      setScanResult({
        confidence: "high",
        document_type: "manual_cf",
        fiscal_code_valid: true,
      });
      setScanMode(null);
      toast.success(language === "it" ? "Codice fiscale valido · dati derivati" : "Valid CF · data derived");
      _checkDuplicates({ fiscal_code: r.data.fiscal_code });
    } catch (err) {
      toast.error(err.response?.data?.detail || (language === "it" ? "Errore" : "Error"));
    } finally { setScanBusy(false); }
  };

  // Cleanup camera on dialog close
  useEffect(() => {
    if (!dialogOpen) _stopCamera();
    return () => _stopCamera();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dialogOpen]);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat(language === "it" ? "it-IT" : "en-US", {
      style: "currency",
      currency: "EUR"
    }).format(amount);
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin w-8 h-8 border-2 border-accent border-t-transparent rounded-full"></div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-3xl font-serif font-medium">{t("clients")}</h1>
            <p className="text-muted-foreground mt-1">
              {language === "it" ? "Gestione clienti della tua attività" : "Your business client management"}
            </p>
          </div>
          
          <div className="flex gap-2">
            <Button 
              variant="outline" 
              onClick={() => window.open(`${API}/clients/export/csv`, '_blank')}
              data-testid="export-clients-btn"
            >
              <Download className="w-4 h-4 mr-2" />
              {language === "it" ? "Esporta CSV" : "Export CSV"}
            </Button>
            
            {canScanIntake && (
              <Button
                onClick={() => navigate("/dashboard/dental/intake")}
                className="rounded-full btn-lift bg-gradient-to-r from-violet-600 to-cyan-600 text-white hover:shadow-2xl hover:from-violet-700 hover:to-cyan-700"
                data-testid="scan-document-btn"
                title={isDental ? "Smart Patient Intake™ · Dental Suite" : "Smart Patient Intake™ · Pro Suite"}
              >
                <ScanLine className="w-4 h-4 mr-2" />
                {language === "it" ? "Scansiona Documento" : "Scan Document"}
                <Crown className="w-3 h-3 ml-1.5 opacity-70" />
              </Button>
            )}

            {isDental && (
              <Button
                variant="outline"
                onClick={() => navigate("/dashboard/dental/consents/templates")}
                className="rounded-full"
                data-testid="consent-templates-btn"
                title="Template moduli per firma digitale"
              >
                <FileSignature className="w-4 h-4 mr-2" />
                {language === "it" ? "Template firme" : "Sign templates"}
              </Button>
            )}

            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button className="rounded-full btn-lift" data-testid="add-client-btn">
                <Plus className="w-4 h-4 mr-2" />
                {t("add")} {language === "it" ? "cliente" : "client"}
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg max-h-[92vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>
                  {language === "it" ? "Nuovo Cliente" : "New Client"}
                </DialogTitle>
              </DialogHeader>

              {canScanIntake && (
                <div className="mt-3 rounded-2xl border border-blue-200 dark:border-blue-900/40 bg-gradient-to-br from-blue-50 via-cyan-50 to-violet-50 dark:from-blue-900/15 dark:via-cyan-900/15 dark:to-violet-900/15 p-3" data-testid="tessera-scanner">
                  <div className="flex items-center justify-between gap-2 mb-2">
                    <div className="flex items-center gap-2">
                      <div className="w-7 h-7 rounded-xl bg-gradient-to-br from-blue-500 to-cyan-500 flex items-center justify-center">
                        <ScanLine className="w-3.5 h-3.5 text-white" />
                      </div>
                      <div>
                        <p className="text-[11px] uppercase tracking-wider font-bold text-blue-700 dark:text-blue-300">{language === "it" ? "Scansione Tessera Sanitaria" : "Health Card Scan"}</p>
                        <p className="text-[10px] text-slate-500">{language === "it" ? "Lettura ottica AI · autocompila i dati" : "AI OCR · auto-fills data"}</p>
                      </div>
                    </div>
                    {scanResult && (
                      <span className="inline-flex items-center text-[9px] font-bold uppercase px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300">
                        ✓ {scanResult.confidence || "ok"}
                      </span>
                    )}
                  </div>

                  {!scanMode && (
                    <div className="grid grid-cols-3 gap-2" data-testid="tessera-mode-picker">
                      <button type="button" onClick={handleStartCamera}
                        className="flex flex-col items-center gap-1 p-2.5 rounded-xl border border-blue-300 dark:border-blue-700 hover:bg-white dark:hover:bg-slate-900/40 transition-colors"
                        data-testid="tessera-mode-camera">
                        <span className="text-lg">📸</span>
                        <span className="text-[10px] font-bold uppercase tracking-wider">{language === "it" ? "Fotocamera" : "Camera"}</span>
                      </button>
                      <label className="flex flex-col items-center gap-1 p-2.5 rounded-xl border border-blue-300 dark:border-blue-700 hover:bg-white dark:hover:bg-slate-900/40 transition-colors cursor-pointer"
                        data-testid="tessera-mode-upload">
                        <span className="text-lg">📁</span>
                        <span className="text-[10px] font-bold uppercase tracking-wider">{language === "it" ? "Carica foto" : "Upload"}</span>
                        <input type="file" accept="image/*" className="hidden" onChange={handleUploadScan} data-testid="tessera-upload-input" />
                      </label>
                      <button type="button" onClick={() => setScanMode("cf")}
                        className="flex flex-col items-center gap-1 p-2.5 rounded-xl border border-blue-300 dark:border-blue-700 hover:bg-white dark:hover:bg-slate-900/40 transition-colors"
                        data-testid="tessera-mode-cf">
                        <span className="text-lg">🆔</span>
                        <span className="text-[10px] font-bold uppercase tracking-wider">{language === "it" ? "Codice F." : "Fiscal C."}</span>
                      </button>
                    </div>
                  )}

                  {scanMode === "camera" && (
                    <div className="space-y-2" data-testid="tessera-camera-panel">
                      <div className="relative rounded-xl overflow-hidden bg-slate-900 aspect-video">
                        <video ref={cameraVideoRef} className="w-full h-full object-cover" playsInline muted />
                        <div className="absolute inset-4 border-2 border-dashed border-white/50 rounded-lg pointer-events-none" />
                      </div>
                      <div className="flex gap-2">
                        <Button type="button" variant="outline" size="sm" onClick={() => { _stopCamera(); setScanMode(null); }} className="flex-1">
                          {language === "it" ? "Annulla" : "Cancel"}
                        </Button>
                        <Button type="button" size="sm" onClick={handleCaptureCamera} disabled={scanBusy}
                          className="flex-1 bg-gradient-to-r from-blue-500 to-cyan-500 text-white"
                          data-testid="tessera-camera-capture">
                          {scanBusy
                            ? (language === "it" ? "Estrazione…" : "Extracting…")
                            : (language === "it" ? "Scatta e estrai" : "Capture & extract")}
                        </Button>
                      </div>
                    </div>
                  )}

                  {scanMode === "cf" && (
                    <div className="space-y-2" data-testid="tessera-cf-panel">
                      <Input
                        value={cfInput}
                        onChange={(e) => setCfInput(e.target.value.toUpperCase())}
                        placeholder="RSSMRA80A01H501U"
                        maxLength={16}
                        className="font-mono tracking-wider text-center text-sm"
                        data-testid="tessera-cf-input"
                      />
                      <div className="flex gap-2">
                        <Button type="button" variant="outline" size="sm" onClick={() => setScanMode(null)} className="flex-1">
                          {language === "it" ? "Annulla" : "Cancel"}
                        </Button>
                        <Button type="button" size="sm" onClick={handleEnrichCF} disabled={scanBusy || cfInput.length !== 16}
                          className="flex-1 bg-gradient-to-r from-blue-500 to-cyan-500 text-white"
                          data-testid="tessera-cf-confirm">
                          {scanBusy
                            ? (language === "it" ? "Verifica…" : "Checking…")
                            : (language === "it" ? "Deriva dati" : "Derive data")}
                        </Button>
                      </div>
                    </div>
                  )}

                  {scanBusy && scanMode === null && (
                    <p className="text-xs text-blue-600 text-center mt-2 animate-pulse">
                      {language === "it" ? "Lettura AI in corso…" : "AI extraction in progress…"}
                    </p>
                  )}
                </div>
              )}

              {/* DUPLICATES BANNER */}
              {isDental && duplicates.length > 0 && (
                <div className="mt-3 rounded-2xl border-2 border-amber-300 dark:border-amber-700 bg-gradient-to-br from-amber-50 to-orange-50 dark:from-amber-900/15 dark:to-orange-900/15 p-3" data-testid="dup-banner">
                  <div className="flex items-center gap-2 mb-2">
                    <div className="w-7 h-7 rounded-xl bg-gradient-to-br from-amber-500 to-orange-500 flex items-center justify-center">
                      <AlertTriangle className="w-3.5 h-3.5 text-white" />
                    </div>
                    <div>
                      <p className="text-[11px] uppercase tracking-wider font-bold text-amber-700 dark:text-amber-300">
                        {language === "it"
                          ? `${duplicates.length} cliente${duplicates.length > 1 ? "i" : ""} già presente${duplicates.length > 1 ? "i" : ""}`
                          : `${duplicates.length} existing client${duplicates.length > 1 ? "s" : ""} found`}
                      </p>
                      <p className="text-[10px] text-slate-500">
                        {language === "it" ? "Apri la scheda esistente o continua a creare un nuovo cliente." : "Open the existing record or continue creating a new client."}
                      </p>
                    </div>
                  </div>
                  <ul className="space-y-1.5">
                    {duplicates.map(d => {
                      const fullName = (d.name || `${d.first_name || ""} ${d.last_name || ""}`).trim() || "—";
                      return (
                        <li key={d.client_id} className="flex items-center justify-between gap-2 rounded-xl bg-white/70 dark:bg-slate-900/40 p-2"
                          data-testid={`dup-item-${d.client_id}`}>
                          <div className="min-w-0">
                            <p className="text-sm font-semibold text-slate-800 dark:text-slate-100 truncate">{fullName}</p>
                            <p className="text-[10px] font-mono text-slate-400 truncate">
                              {d.fiscal_code || d.email || d.phone || "—"}
                              {d.birth_date && <span className="ml-2">· {d.birth_date}</span>}
                            </p>
                          </div>
                          <Button
                            type="button" size="sm"
                            onClick={() => { setDialogOpen(false); resetForm(); navigate(`/dashboard/client/${d.client_id}`); }}
                            className="h-7 rounded-full text-[11px] bg-gradient-to-r from-amber-500 to-orange-500 text-white whitespace-nowrap"
                            data-testid={`dup-open-${d.client_id}`}
                          >
                            {language === "it" ? "Apri" : "Open"} <ChevronRight className="w-3 h-3 ml-0.5" />
                          </Button>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-4 mt-4">
                <div>
                  <label className="block text-sm font-medium mb-1.5">{t("name")} *</label>
                  <Input
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder={language === "it" ? "Nome cliente" : "Client name"}
                    required
                    data-testid="client-name-input"
                  />
                </div>

                {isDental && (
                  <div className="grid grid-cols-2 gap-3" data-testid="dental-extra-fields">
                    <div>
                      <label className="block text-sm font-medium mb-1.5">{language === "it" ? "Codice Fiscale" : "Fiscal Code"}</label>
                      <Input
                        value={formData.fiscal_code}
                        onChange={(e) => setFormData({ ...formData, fiscal_code: e.target.value.toUpperCase() })}
                        placeholder="RSSMRA80A01H501Z"
                        maxLength={16}
                        className="font-mono text-xs"
                        data-testid="client-cf-input"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1.5">{language === "it" ? "Data nascita" : "Birth date"}</label>
                      <Input
                        type="date"
                        value={formData.birth_date}
                        onChange={(e) => setFormData({ ...formData, birth_date: e.target.value })}
                        data-testid="client-birthdate-input"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1.5">{language === "it" ? "Sesso" : "Gender"}</label>
                      <select
                        value={formData.gender}
                        onChange={(e) => setFormData({ ...formData, gender: e.target.value })}
                        className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                        data-testid="client-gender-input"
                      >
                        <option value="">—</option>
                        <option value="M">M</option>
                        <option value="F">F</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1.5">{language === "it" ? "Luogo nascita" : "Birth place"}</label>
                      <Input
                        value={formData.birth_place}
                        onChange={(e) => setFormData({ ...formData, birth_place: e.target.value })}
                        placeholder={language === "it" ? "Milano" : "City"}
                        data-testid="client-birthplace-input"
                      />
                    </div>
                  </div>
                )}

                <div>
                  <label className="block text-sm font-medium mb-1.5">{t("email")}</label>
                  <Input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    placeholder="email@example.com"
                    data-testid="client-email-input"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-1.5">{t("phone")}</label>
                  <Input
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    placeholder="+39 123 456 7890"
                    data-testid="client-phone-input"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-1.5">{t("notes")}</label>
                  <Input
                    value={formData.notes}
                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                    placeholder={language === "it" ? "Note sul cliente" : "Client notes"}
                    data-testid="client-notes-input"
                  />
                </div>

                <div className="flex justify-end gap-3 pt-4">
                  <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                    {t("cancel")}
                  </Button>
                  <Button type="submit" data-testid="save-client-btn">
                    {t("save")}
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
          </div>
        </div>

        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder={t("search")}
              className="pl-10"
              data-testid="client-search-input"
            />
          </div>
          
          <div className="flex gap-2 overflow-x-auto">
            {tags.map((tag) => (
              <Button
                key={tag.value}
                variant={selectedTag === tag.value ? "default" : "outline"}
                size="sm"
                className="rounded-full"
                onClick={() => setSelectedTag(tag.value)}
                data-testid={`filter-tag-${tag.value || "all"}`}
              >
                {tag.label}
              </Button>
            ))}
          </div>
        </div>

        {/* Client Detail Dialog */}
        <Dialog open={!!selectedClient} onOpenChange={(open) => { if (!open) { setSelectedClient(null); setLtvData(null); } }}>
          <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
            <DialogHeader>
              <div className="flex items-center gap-3">
                <DialogTitle>{clientDetails?.name}</DialogTitle>
                {ltvData && <SegmentBadge segment={ltvData.segment} language={language} />}
              </div>
            </DialogHeader>
            
            {clientDetails && (
              <div className="mt-4 space-y-6">
                {/* Stats */}
                <div className="grid grid-cols-3 gap-4">
                  <div className="p-4 rounded-xl bg-muted/50 text-center">
                    <p className="text-2xl font-mono font-bold">{clientDetails.total_visits}</p>
                    <p className="text-sm text-muted-foreground">{language === "it" ? "Visite" : "Visits"}</p>
                  </div>
                  <div className="p-4 rounded-xl bg-muted/50 text-center">
                    <p className="text-2xl font-mono font-bold">{formatCurrency(clientDetails.total_spent)}</p>
                    <p className="text-sm text-muted-foreground">{language === "it" ? "Speso" : "Spent"}</p>
                  </div>
                  <div className="p-4 rounded-xl bg-muted/50 text-center">
                    <p className="text-2xl font-mono font-bold">
                      {clientDetails.last_visit 
                        ? format(new Date(clientDetails.last_visit), "dd/MM")
                        : "-"
                      }
                    </p>
                    <p className="text-sm text-muted-foreground">{language === "it" ? "Ultima visita" : "Last visit"}</p>
                  </div>
                </div>

                {/* LTV Section */}
                <div className="rounded-xl border border-slate-200 dark:border-slate-700 overflow-hidden" data-testid="client-ltv-section">
                  <div className="px-4 py-3 bg-slate-50 dark:bg-slate-800/50 flex items-center justify-between">
                    <h4 className="font-semibold flex items-center gap-2 text-sm">
                      <Wallet className="w-4 h-4 text-violet-500" />
                      {language === "it" ? "Valore Cliente (LTV)" : "Customer Value (LTV)"}
                    </h4>
                    {ltvLoading && <RefreshCw className="w-4 h-4 animate-spin text-muted-foreground" />}
                  </div>
                  
                  {ltvData ? (
                    <div className="p-4 space-y-4">
                      {/* LTV Metrics Grid */}
                      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                        <div className="p-3 rounded-lg bg-emerald-50 dark:bg-emerald-950/20" data-testid="ltv-total-spent">
                          <p className="text-xs text-emerald-600 dark:text-emerald-400 font-medium mb-1">
                            {language === "it" ? "Totale Speso" : "Total Spent"}
                          </p>
                          <p className="text-lg font-bold text-emerald-700 dark:text-emerald-300">{formatCurrency(ltvData.total_spent)}</p>
                        </div>
                        <div className="p-3 rounded-lg bg-blue-50 dark:bg-blue-950/20" data-testid="ltv-avg-ticket">
                          <p className="text-xs text-blue-600 dark:text-blue-400 font-medium mb-1">
                            {language === "it" ? "Ticket Medio" : "Avg Ticket"}
                          </p>
                          <p className="text-lg font-bold text-blue-700 dark:text-blue-300">{formatCurrency(ltvData.average_ticket)}</p>
                        </div>
                        <div className="p-3 rounded-lg bg-violet-50 dark:bg-violet-950/20" data-testid="ltv-visit-count">
                          <p className="text-xs text-violet-600 dark:text-violet-400 font-medium mb-1">
                            {language === "it" ? "Visite" : "Visits"}
                          </p>
                          <p className="text-lg font-bold text-violet-700 dark:text-violet-300">{ltvData.visit_count}</p>
                        </div>
                        <div className="p-3 rounded-lg bg-orange-50 dark:bg-orange-950/20" data-testid="ltv-frequency">
                          <p className="text-xs text-orange-600 dark:text-orange-400 font-medium mb-1">
                            {language === "it" ? "Frequenza" : "Frequency"}
                          </p>
                          <p className="text-lg font-bold text-orange-700 dark:text-orange-300">
                            {ltvData.visit_frequency_days ? `${ltvData.visit_frequency_days}gg` : "-"}
                          </p>
                        </div>
                      </div>

                      {/* Last Visit & Days Since */}
                      {ltvData.last_visit && (
                        <div className="flex items-center gap-4 text-sm text-muted-foreground">
                          <span className="flex items-center gap-1.5">
                            <Calendar className="w-3.5 h-3.5" />
                            {language === "it" ? "Ultima visita:" : "Last visit:"} {ltvData.last_visit}
                          </span>
                          {ltvData.days_since_last_visit != null && (
                            <span className={`flex items-center gap-1.5 ${ltvData.days_since_last_visit > 60 ? "text-red-500" : ""}`}>
                              <Clock className="w-3.5 h-3.5" />
                              {ltvData.days_since_last_visit} {language === "it" ? "giorni fa" : "days ago"}
                            </span>
                          )}
                        </div>
                      )}

                      {/* Smart Suggestion */}
                      {ltvData.suggestion && (
                        <div 
                          className="flex gap-3 p-3 rounded-lg bg-amber-50 dark:bg-amber-950/20 border border-amber-200 dark:border-amber-800"
                          data-testid="ltv-suggestion"
                        >
                          <Sparkles className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
                          <p className="text-sm text-amber-800 dark:text-amber-200">{ltvData.suggestion}</p>
                        </div>
                      )}
                    </div>
                  ) : !ltvLoading ? (
                    <div className="p-6 text-center text-muted-foreground text-sm">
                      {language === "it" ? "Nessun dato LTV disponibile" : "No LTV data available"}
                    </div>
                  ) : null}
                </div>

                {/* Contact Info */}
                <div className="space-y-2">
                  {clientDetails.email && (
                    <p className="flex items-center gap-2 text-sm">
                      <Mail className="w-4 h-4 text-muted-foreground" />
                      {clientDetails.email}
                    </p>
                  )}
                  {clientDetails.phone && (
                    <p className="flex items-center gap-2 text-sm">
                      <Phone className="w-4 h-4 text-muted-foreground" />
                      {clientDetails.phone}
                    </p>
                  )}
                </div>

                {/* Tags */}
                {clientDetails.tags?.length > 0 && (
                  <div className="flex flex-wrap gap-2">
                    {clientDetails.tags.map((tag) => (
                      <span 
                        key={tag}
                        className={`px-2 py-1 rounded-full text-xs font-medium ${
                          tag === "vip" ? "badge-warning" :
                          tag === "new" ? "badge-info" :
                          tag === "frequent" ? "badge-success" :
                          "bg-muted text-muted-foreground"
                        }`}
                      >
                        {tag.toUpperCase()}
                      </span>
                    ))}
                  </div>
                )}

                {/* Recent Appointments */}
                <div>
                  <h4 className="font-medium mb-3">{language === "it" ? "Storico appuntamenti" : "Appointment history"}</h4>
                  <div className="space-y-2 max-h-48 overflow-y-auto">
                    {clientDetails.appointments?.length > 0 ? (
                      clientDetails.appointments.map((appt) => (
                        <div 
                          key={appt.appointment_id}
                          className="flex items-center justify-between p-3 rounded-lg bg-muted/50"
                        >
                          <div>
                            <p className="text-sm font-medium">{appt.date} - {appt.time}</p>
                          </div>
                          <span className={`px-2 py-0.5 rounded-full text-xs ${
                            appt.status === "completed" ? "badge-success" :
                            appt.status === "cancelled" ? "badge-error" :
                            "badge-warning"
                          }`}>
                            {t(appt.status)}
                          </span>
                        </div>
                      ))
                    ) : (
                      <p className="text-sm text-muted-foreground text-center py-4">
                        {language === "it" ? "Nessun appuntamento" : "No appointments"}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Clients Table */}
        <div className="bg-card rounded-2xl border overflow-hidden">
          <div className="overflow-x-auto">
            <table className="data-table">
              <thead>
                <tr>
                  <th>{t("name")}</th>
                  <th className="hide-mobile">{t("email")}</th>
                  <th className="hide-mobile">{t("phone")}</th>
                  <th>{language === "it" ? "Visite" : "Visits"}</th>
                  <th>{language === "it" ? "Speso" : "Spent"}</th>
                  <th className="hide-mobile">{language === "it" ? "Segmento" : "Segment"}</th>
                  <th>Tags</th>
                </tr>
              </thead>
              <tbody>
                {clients.map((client) => (
                  <tr 
                    key={client.client_id}
                    className="cursor-pointer"
                    onClick={() => navigate(`/dashboard/client/${client.client_id}`)}
                    data-testid={`client-row-${client.client_id}`}
                  >
                    <td>
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-full bg-accent/10 flex items-center justify-center">
                          <span className="text-sm font-medium text-accent">{(client.name || `${client.first_name || ""} ${client.last_name || ""}`.trim() || "?")[0]}</span>
                        </div>
                        <span className="font-medium">{client.name || `${client.first_name || ""} ${client.last_name || ""}`.trim() || "—"}</span>
                      </div>
                    </td>
                    <td className="hide-mobile text-muted-foreground">{client.email || "-"}</td>
                    <td className="hide-mobile text-muted-foreground">{client.phone || "-"}</td>
                    <td className="font-mono">{client.total_visits}</td>
                    <td className="font-mono">{formatCurrency(client.total_spent)}</td>
                    <td className="hide-mobile">
                      {client.ltv_data?.segment && (
                        <SegmentBadge segment={client.ltv_data.segment} language={language} />
                      )}
                    </td>
                    <td>
                      <div className="flex gap-1">
                        {client.tags?.slice(0, 2).map((tag) => (
                          <span 
                            key={tag}
                            className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                              tag === "vip" ? "badge-warning" :
                              tag === "new" ? "badge-info" :
                              tag === "frequent" ? "badge-success" :
                              "bg-muted text-muted-foreground"
                            }`}
                          >
                            {tag}
                          </span>
                        ))}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {clients.length === 0 && (
          <div className="text-center py-12">
            <p className="text-muted-foreground">
              {language === "it" ? "Nessun cliente trovato" : "No clients found"}
            </p>
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default Clients;
